import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { MantineProvider } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { DatesProvider } from "@mantine/dates";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { theme } from "./theme";
import { AutoRefreshProvider } from "./autoRefresh";
import { AuthProvider } from "./auth";
import { MaskingConfigProvider } from "./maskingConfig";
import ErrorBoundary from "./components/ErrorBoundary";

import "@mantine/core/styles.css";
import "@mantine/charts/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dates/styles.css";
import "./styles.css";

// Auto-recover from "stale tab + new deploy" chunk-load failures.
//
// When Pages deploys a new build, the hashed chunk filenames change. A tab
// that already loaded the OLD index.html will try to fetch the OLD chunks
// on first navigation → 404. Without this, the user sees the ErrorBoundary's
// generic "Something went wrong" screen and has to hard-refresh manually.
//
// We listen for both Vite's preload error event AND raw chunk fetch failures
// (covers the lazy() path in App.tsx). On hit, set a sessionStorage flag so
// we don't reload-loop, then force a hard reload.
const RELOAD_FLAG = "wptw:chunk-reload-fired";
function reloadOnceForStaleChunks(reason: string): void {
  try {
    if (window.sessionStorage.getItem(RELOAD_FLAG) === "1") {
      // Already reloaded once this tab session — don't loop. Let the
      // ErrorBoundary show so the user sees something instead of a
      // refresh loop on a genuinely broken chunk.
      console.error("Chunk load failed after reload — not retrying:", reason);
      return;
    }
    window.sessionStorage.setItem(RELOAD_FLAG, "1");
  } catch { /* sessionStorage blocked — still try the reload */ }
  console.warn("Stale chunk detected, reloading:", reason);
  window.location.reload();
}

window.addEventListener("vite:preloadError", (event) => {
  reloadOnceForStaleChunks(`vite:preloadError ${String((event as Event & { payload?: unknown }).payload ?? "")}`);
});

window.addEventListener("error", (event) => {
  // Vite chunk-fetch failures sometimes surface here with a generic
  // "Failed to fetch dynamically imported module" message. Catch them too.
  const msg = String(event.message ?? "");
  if (msg.includes("Failed to fetch dynamically imported module") || msg.includes("error loading dynamically imported module")) {
    reloadOnceForStaleChunks(msg);
  }
});

window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason;
  const msg = reason instanceof Error ? reason.message : String(reason ?? "");
  if (msg.includes("Failed to fetch dynamically imported module") || msg.includes("error loading dynamically imported module")) {
    reloadOnceForStaleChunks(msg);
  }
});

// Clear the once-per-session flag on a successful render. If the app mounts
// without error after a reload, we know the new build loaded fine and we
// should be willing to retry again next time.
if (typeof window !== "undefined") {
  window.setTimeout(() => {
    try { window.sessionStorage.removeItem(RELOAD_FLAG); } catch { /* ignore */ }
  }, 5000);
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <DatesProvider settings={{ locale: "en" }}>
        <Notifications />
        <ErrorBoundary>
          <MaskingConfigProvider>
            <AuthProvider>
              <AutoRefreshProvider>
                <BrowserRouter>
                  <App />
                </BrowserRouter>
              </AutoRefreshProvider>
            </AuthProvider>
          </MaskingConfigProvider>
        </ErrorBoundary>
      </DatesProvider>
    </MantineProvider>
  </StrictMode>,
);
